
import { ResellerOrder, WaitingPeriodStatus } from "@/types/resellerOrder";
import { resellerOrders } from "./types";
import { generateLicenseKey } from "../licenseService/licenseKeys";

// Toggle the waiting period status
export const toggleWaitingPeriod = async (
  orderId: string, 
  status: WaitingPeriodStatus
): Promise<ResellerOrder> => {
  // Simulate API request delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const orderIndex = resellerOrders.findIndex(order => order.id === orderId);
  if (orderIndex === -1) {
    throw new Error("Order not found");
  }
  
  const updatedOrder = {
    ...resellerOrders[orderIndex],
    waitingPeriod: status
  };
  
  // If changing to active, set a scheduled time 5 minutes in the future
  if (status === 'active') {
    const activationTime = new Date();
    activationTime.setMinutes(activationTime.getMinutes() + 5);
    updatedOrder.scheduledActivationTime = activationTime;
  } else if (status === 'paused' || status === 'manual') {
    // If pausing or setting to manual, clear the scheduled time
    delete updatedOrder.scheduledActivationTime;
  }
  
  resellerOrders[orderIndex] = updatedOrder;
  return updatedOrder;
};

// Verify payment transaction
export const verifyPaymentTransaction = async (
  orderId: string, 
  transactionId: string
): Promise<{ verified: boolean; message?: string }> => {
  // Simulate API request delay
  await new Promise(resolve => setTimeout(resolve, 700));
  
  const orderIndex = resellerOrders.findIndex(order => order.id === orderId);
  if (orderIndex === -1) {
    return { verified: false, message: "Order not found" };
  }
  
  // For demonstration purposes, all transactions are verified
  resellerOrders[orderIndex] = {
    ...resellerOrders[orderIndex],
    paymentVerified: true
  };
  
  return { verified: true };
};

// Approve an order
export const approveOrder = async (orderId: string, reason: string): Promise<ResellerOrder> => {
  // Simulate API request delay
  await new Promise(resolve => setTimeout(resolve, 600));
  
  const orderIndex = resellerOrders.findIndex(order => order.id === orderId);
  if (orderIndex === -1) {
    throw new Error("Order not found");
  }
  
  // Set activation time 5 minutes from now
  const activationTime = new Date();
  activationTime.setMinutes(activationTime.getMinutes() + 5);
  
  const updatedOrder = {
    ...resellerOrders[orderIndex],
    status: 'approved' as const,
    waitingPeriod: 'active' as const,
    scheduledActivationTime: activationTime,
    notes: reason || "Order approved"
  };
  
  resellerOrders[orderIndex] = updatedOrder;
  return updatedOrder;
};

// Reject an order
export const rejectOrder = async (orderId: string, reason: string): Promise<ResellerOrder> => {
  // Simulate API request delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const orderIndex = resellerOrders.findIndex(order => order.id === orderId);
  if (orderIndex === -1) {
    throw new Error("Order not found");
  }
  
  const updatedOrder = {
    ...resellerOrders[orderIndex],
    status: 'rejected' as const,
    notes: reason
  };
  
  resellerOrders[orderIndex] = updatedOrder;
  return updatedOrder;
};

// Put an order on hold
export const holdOrder = async (orderId: string, reason: string): Promise<ResellerOrder> => {
  // Simulate API request delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const orderIndex = resellerOrders.findIndex(order => order.id === orderId);
  if (orderIndex === -1) {
    throw new Error("Order not found");
  }
  
  const updatedOrder = {
    ...resellerOrders[orderIndex],
    status: 'hold' as const,
    notes: reason
  };
  
  resellerOrders[orderIndex] = updatedOrder;
  return updatedOrder;
};
